// App.js
// Propósito: Orquestar la UI principal: input de URL, loader y resultados.
// Explicación: Separamos componentes y lógica de fetch para mantener claridad.

import React, { useState } from 'react';
import UrlInput from './components/UrlInput.js';
import ScoreBadge from './components/ScoreBadge.js';
import ReportTable from './components/ReportTable.js';
import Navbar from './components/Navbar.js';
import AuthModal from './components/AuthModal.js';
import WhatsAppButton from './components/WhatsAppButton.js';
import EnumerationView from './views/EnumerationView.js';
import EmailAnalyzerView from './views/EmailAnalyzerView.js';
import ProfileView from './views/ProfileView.js';
import LabsView from './views/LabsView.js';
import HomeView from './views/HomeView.js';
import AuthCallback from './views/AuthCallback.js';
import { analyzeUrl } from './services/api.js';
import { useAuth } from './context/AuthContext.js';
import { LabsProvider } from './context/LabsContext.js';

function App() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [showAuthModal, setShowAuthModal] = useState(false);

  async function handleAnalyze(url) {
    setError(null);
    setLoading(true);
    setReport(null);
    try {
      const data = await analyzeUrl(url);
      setReport(data);
    } catch (e) {
      setError(e.message || 'No se pudo analizar la URL');
    } finally {
      setLoading(false);
    }
  }

  // Handle OAuth callback route
  if (typeof window !== 'undefined' && window.location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  return (
    <LabsProvider>
      <div className="container py-4 app-shell">
        <Navbar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onRequireAuth={() => setShowAuthModal(true)}
        />
        <AuthModal show={showAuthModal} onClose={() => {
          setShowAuthModal(false);
          if (user) setActiveTab('enum');
        }} />

      {activeTab === 'home' && (
        <HomeView onNavigate={setActiveTab} />
      )}

      {activeTab === 'basic' && (
        <>
          <h1 className="mb-2 app-title">URL Security Analyzer</h1>
          <p className="text-muted app-subtitle">Ingrese una URL para analizar cabeceras, contenido, CORS y TLS.</p>
          <UrlInput onAnalyze={handleAnalyze} />
        </>
      )}

      {activeTab === 'enum' && (
        user ? (
          <EnumerationView />
        ) : (
          <div className="alert alert-info glass-card mt-3">
            Para usar Enumeración, inicia sesión o regístrate.
          </div>
        )
      )}

      {activeTab === 'email' && <EmailAnalyzerView />}

      {activeTab === 'labs' && (
        user ? (
          <LabsView />
        ) : (
          <div className="alert alert-info glass-card mt-3">
            Para acceder a Labs, inicia sesión o regístrate.
          </div>
        )
      )}

      {activeTab === 'profile' && <ProfileView />}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner" role="status" aria-label="Analizando">
            <div className="dot" />
            <div className="dot" />
            <div className="dot" />
          </div>
          <span className="loading-text">Analizando, por favor espere…</span>
        </div>
      )}

      {error && (
        <div className="alert alert-danger glass-card mt-3" role="alert">
          {error}
        </div>
      )}

      {activeTab === 'basic' && report && (
        <div className="mt-4 glass-card result-card">
          <div className="d-flex align-items-center gap-3 score-row">
            <ScoreBadge score={Number(report.score || 0)} />
            <small className="text-muted">Timestamp: {report.timestamp}</small>
          </div>
          <div className="score-scale glass-card-lite mt-3">
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span>0-33 Inseguro</span>
              <span>34-66 Riesgo moderado</span>
              <span>67-100 Aceptable</span>
            </div>
            <div className="scale-bar">
              <div className="scale-segment seg-danger" />
              <div className="scale-segment seg-warning" />
              <div className="scale-segment seg-success" />
              <div
                className="scale-indicator"
                style={{ left: `${Math.max(0, Math.min(100, Number(report.score || 0)))}%` }}
              />
            </div>
          </div>
          <div className="mt-3">
            <ReportTable
              vulnerabilities={report.vulnerabilities || []}
              warnings={report.warnings || []}
              improvements={report.improvements || []}
            />
          </div>
        </div>
      )}

      <footer className="app-footer">
        <p>
          Creado por <strong>Emmanuel Cabrales</strong> — Backend & Security-focused Developer
        </p>
        <p>
          ¿Necesitas ayuda con la seguridad de tu aplicación?
          <a href="https://www.linkedin.com/in/emmanuel-c-fuentes-1019a8120/" target="_blank" rel="noreferrer">
            &nbsp;Contáctame en LinkedIn
          </a>
        </p>
      </footer>
      
      {/* Botón flotante de WhatsApp */}
      <WhatsAppButton />
    </div>
    </LabsProvider>
  );
}

export default App;
